// GenererDmca — dedikert side for DMCA-brev-generator (åpen for alle innloggede).
// Kan lenkes direkte til: /dmca
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Copy, Download, Shield } from "lucide-react";
import Layout from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";

const cleanDomain = (d) => (d || "")
  .trim()
  .replace(/^https?:\/\//i, "")
  .replace(/\/.*$/, "")
  .replace(/^www\./i, "");

export default function GenererDmca() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    infringing_url: "",
    infringing_domain: "",
    contact_email: user?.email || "",
    contact_phone: "",
    full_name: "",
    original_url: "",
    additional_notes: "",
  });
  const [letter, setLetter] = useState(null);
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    if (!form.infringing_url || !form.contact_email || !form.full_name.trim()) {
      toast.error("Navn, URL og e-post er påkrevd");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/leak-check/dmca-manual", form);
      setLetter(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Feil");
    } finally { setBusy(false); }
  };

  const download = () => {
    const blob = new Blob([letter.letter], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = letter.filename;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const copy = async () => {
    await navigator.clipboard.writeText(letter.letter);
    toast.success("Kopiert til utklippstavle");
  };

  const reset = () => setLetter(null);

  return (
    <Layout>
      <div className="max-w-3xl mx-auto py-4">
        <Link to="/leak-sjekk" className="inline-flex items-center gap-1 text-[var(--cream-soft)] text-xs hover:text-[var(--magenta)] mb-3">
          <ArrowLeft className="h-3 w-3" /> Tilbake til Leak-sjekk
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <Shield className="h-6 w-6 text-[var(--magenta)]" />
          <h1 className="font-fraunces italic text-3xl text-[var(--cream)]">DMCA-brev-generator</h1>
        </div>
        <p className="text-[var(--cream-soft)] text-sm mb-6 max-w-2xl">
          Fant du innholdet ditt på et leak-nettsted? Fyll ut skjema, så genererer vi et ferdig DMCA-brev
          du kan sende til nettstedet, hosting-leverandøren og Google. Åpen for alle brukere.
        </p>

        <div className="editorial-card p-6" data-testid="dmca-page">
          {!letter ? (
            <div className="space-y-3">
              <div>
                <label className="text-xs text-[var(--cream-soft)]">URL til lekkasje *</label>
                <input value={form.infringing_url} onChange={(e) => setForm({ ...form, infringing_url: e.target.value })} placeholder="https://leak-site.com/…" className="input-editorial w-full" data-testid="dmca-url" />
              </div>
              <div>
                <label className="text-xs text-[var(--cream-soft)]">Domene (valgfritt)</label>
                <input value={form.infringing_domain} onChange={(e) => setForm({ ...form, infringing_domain: e.target.value })} placeholder="leak-site.com" className="input-editorial w-full" />
              </div>
              <div>
                <label className="text-xs text-[var(--cream-soft)]">URL til originalt innhold (valgfritt)</label>
                <input value={form.original_url} onChange={(e) => setForm({ ...form, original_url: e.target.value })} placeholder="https://onlyfans.com/dittbrukernavn eller https://norskerotikk.no/profil/dittbrukernavn" className="input-editorial w-full" data-testid="dmca-original-url" />
                <div className="text-[10px] text-[var(--cream-faint)] mt-1">Peker til der originalen er publisert lovlig — profil, OnlyFans, Fansly osv. Hvis blank, står det at originalen er lagret privat hos NorskErotikk.no.</div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[var(--cream-soft)]">Ditt fulle navn *</label>
                  <input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Ola Nordmann" className="input-editorial w-full" data-testid="dmca-name" />
                  <div className="text-[10px] text-[var(--cream-faint)] mt-1">Må være ekte juridisk navn — brukernavn er ikke gyldig for DMCA.</div>
                </div>
                <div>
                  <label className="text-xs text-[var(--cream-soft)]">Din e-post *</label>
                  <input value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="input-editorial w-full" />
                </div>
              </div>
              <div>
                <label className="text-xs text-[var(--cream-soft)]">Telefon (valgfritt)</label>
                <input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder="+47 9XX XX XXX" className="input-editorial w-full" />
              </div>
              <div>
                <label className="text-xs text-[var(--cream-soft)]">Tilleggsnotat (valgfritt)</label>
                <textarea value={form.additional_notes} onChange={(e) => setForm({ ...form, additional_notes: e.target.value })} rows={3} placeholder="Ekstra kontekst til hosten – f.eks. flere URLer med samme bilde, eller referanse til tidligere ubesvarte varsler." className="input-editorial w-full" data-testid="dmca-notes" />
                <div className="text-[10px] text-[var(--cream-faint)] mt-1">Brevet inneholder allerede standard good-faith- og perjury-erklæring på engelsk. Bruk dette feltet kun til ekstra kontekst.</div>
              </div>
              <button onClick={generate} disabled={busy} className="btn-primary w-full" data-testid="generate-dmca">
                {busy ? "Genererer…" : "Generer DMCA-brev"}
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2">
                <button onClick={download} className="btn-primary flex-1 inline-flex items-center justify-center gap-2" data-testid="download-dmca">
                  <Download className="h-4 w-4" /> Last ned .txt
                </button>
                <button onClick={copy} className="btn-ghost flex-1 inline-flex items-center justify-center gap-2" data-testid="copy-dmca">
                  <Copy className="h-4 w-4" /> Kopier tekst
                </button>
                <button onClick={reset} className="btn-ghost">Lag nytt</button>
              </div>
              <pre className="bg-black/40 p-3 text-[10px] text-[var(--cream-soft)] whitespace-pre-wrap font-mono max-h-96 overflow-y-auto border border-white/10">
                {letter.letter}
              </pre>
              <div className="text-xs text-[var(--cream-soft)] leading-relaxed border-t border-white/10 pt-4">
                <b className="text-[var(--cream)]">Hvor sender du brevet?</b>
                <p className="mt-1.5 text-[11px] text-[var(--cream-faint)]">
                  Send til alle tre parallelt for maksimal effekt. Hosting-leverandøren og Google svarer typisk raskere enn selve leak-nettstedet.
                </p>
                <ol className="mt-2 space-y-2 list-decimal list-inside">
                  <li>
                    <b>DMCA-agent på leak-nettstedet</b> — se etter <b>Contact</b>, <b>Legal</b>, <b>DMCA</b> eller <b>Abuse</b> i footeren.
                  </li>
                  <li>
                    <b>Hosting-leverandøren</b> — slå opp domenet på{" "}
                    <a href={cleanDomain(form.infringing_domain) ? `https://who.is/whois/${cleanDomain(form.infringing_domain)}` : "https://who.is"} target="_blank" rel="noopener noreferrer" className="text-[var(--magenta)] underline">who.is</a>{" "}
                    for å finne hosten. Vanlige: abuse@cloudflare.com, abuse@hetzner.com, abuse@ovh.net, abuse@digitalocean.com.
                  </li>
                  <li>
                    <b>Google Search</b> — fyll ut{" "}
                    <a href="https://reportcontent.google.com/forms/dmca_search" target="_blank" rel="noopener noreferrer" className="text-[var(--magenta)] underline">reportcontent.google.com</a>. Innholdet forsvinner fra søk innen 24–72 t.
                  </li>
                </ol>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
